import Tooltip from '@mui/material/Tooltip';
import React, { createRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { pdfApi } from 'api';
import { useModal } from 'shared/hooks';
import { ItemActionModal } from 'shared/ui/ItemActionModal';

import pdfRedSvg from '../../../assets/img/file-pdf-red.svg';
import pdfSvg from '../../../assets/img/file-pdf.svg';

interface Props {
  currentICLId: string;
  id: string;
  studentPdfUrl?: string;
  hasText?: boolean;
  closeModal?: () => void;
}

export const StudentPdfInput = ({ currentICLId, id, studentPdfUrl, hasText, closeModal }: Props) => {
  const { t } = useTranslation();
  const inputRef = createRef<HTMLInputElement>();
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, close, open } = useModal();

  const sendPdf = async () => {
    const pdfInput = inputRef.current;

    if (!pdfInput || !pdfInput.files) {
      return;
    }

    setIsLoading(true);

    const file = pdfInput.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      await pdfApi.addPdf(file, currentICLId, id, true);
      toast.success(t('Successfully added!'));
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsLoading(false);
  };

  const removePdf = async () => {
    if (!studentPdfUrl) {
      return;
    }

    setIsLoading(true);

    try {
      await pdfApi.removePdf(studentPdfUrl, currentICLId, id, true);
      toast.success(t('Successfully removed!'));
      close();
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsLoading(false);
  };

  return (
    <div className="ql-image button-icon">
      <Tooltip
        title={studentPdfUrl ? t('remove pdf') : t('add pdf')}
        componentsProps={{
          popper: {
            sx: {
              ['& .MuiTooltip-arrow']: {
                color: '#9CDFFC',
              },
              ['& .MuiTooltip-tooltip']: {
                backgroundColor: '#9CDFFC',
                color: '#000000',
                fontSize: '14px',
                padding: '14px',
                borderRadius: '8px',
              },
            },
          },
        }}
        arrow
      >
        <button
          onClick={() => {
            closeModal && closeModal();

            if (studentPdfUrl) {
              open();
            } else {
              inputRef.current?.click();
            }
          }}
          className={
            hasText
              ? 'flex items-center gap-[10px]'
              : 'btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600'
          }
        >
          {studentPdfUrl ? <img src={pdfRedSvg} alt="camera" /> : <img src={pdfSvg} alt="camera" />}

          <input
            id="pdfInput"
            type="file"
            className="hidden"
            accept="application/pdf"
            ref={inputRef}
            onChange={sendPdf}
            disabled={isLoading || !!studentPdfUrl}
            title={t('add pdf')}
          />

          {hasText && (
            <p className="!mb-0 text-base font-bold text-[#242C2C]">{studentPdfUrl ? t('remove pdf') : t('add pdf')}</p>
          )}
        </button>
      </Tooltip>

      <ItemActionModal
        isOpen={isOpen}
        close={close}
        title={t('remove pdf')}
        onHandle={removePdf}
        isSubmitButtonDisabled={isLoading}
        buttonTitle="delete"
      />
    </div>
  );
};
